/**
 * Marketing plan catalog.
 *
 * Feature keys and limits mirror the real entitlement catalog in
 * api/database/migrations/add_rbac_platform_and_plans.py:
 *   Free       — all features except voice · 2 users · 1 widget
 *   Pro        — all features                · 15 users · 10 widgets · $15/mo
 *   Enterprise — all features                · unlimited · custom (contact us)
 *
 * Pro/Enterprise checkout isn't self-serve yet — a platform admin activates the
 * plan; Enterprise uses a "request a meeting" CTA.
 */

export const PLANS = [
  {
    slug: 'free', name: 'Free', price: '$0', period: 'free forever',
    tagline: 'Build and test your first knowledge-grounded agent.',
    cta: 'Create free workspace', ctaTo: '/register', highlighted: false,
    limits: { users: '2 team members', widgets: '1 widget' },
    features: [
      'Grounded chat with citations',
      'Knowledge base (crawl + upload)',
      'Conversations & lead capture',
      'Analytics dashboard',
      '1 embeddable widget',
      'Email & SMS new-chat alerts',
    ],
    notIncluded: [],
  },
  {
    slug: 'pro', name: 'Pro', price: '$15', period: 'per month',
    tagline: 'For growing teams that need more seats and widgets.',
    cta: 'Get started', ctaTo: '/register', highlighted: true, badge: 'Most popular',
    limits: { users: '15 team members', widgets: '10 widgets' },
    features: [
      'Everything in Free, plus:',
      '15 team members',
      '10 embeddable widgets',
      'Priority support',
    ],
    notIncluded: [],
  },
  {
    slug: 'enterprise', name: 'Enterprise', price: 'Custom', period: "let's talk",
    tagline: 'Unlimited scale, self-hosting, and hands-on support.',
    cta: 'Request a meeting',
    ctaHref: 'mailto:sales@sagemindlabs.com?subject=SageMindLabs%20Enterprise%20inquiry&body=Hi%20SageMindLabs%20team%2C%20we%27d%20like%20to%20book%20a%20call%20to%20discuss%20an%20Enterprise%20plan.%0D%0A%0D%0ACompany%3A%0D%0ATeam%20size%3A%0D%0A',
    highlighted: false,
    limits: { users: 'Unlimited members', widgets: 'Unlimited widgets' },
    features: [
      'Everything in Pro, plus:',
      'Unlimited members & widgets',
      'Self-host on your own infrastructure',
      'SSO & custom onboarding',
      'Dedicated support & SLA',
    ],
    notIncluded: [],
  },
];

/**
 * Feature comparison matrix for the pricing page.
 * Values: true = included, false = not included, or a string for a specific value.
 */
export const COMPARISON = [
  { label: 'Monthly price', free: '$0', pro: '$15', enterprise: 'Custom' },
  { label: 'Team members', free: '2', pro: '15', enterprise: 'Unlimited' },
  { label: 'Embeddable widgets', free: '1', pro: '10', enterprise: 'Unlimited' },
  { label: 'Grounded chat with citations', free: true, pro: true, enterprise: true },
  { label: 'Knowledge base (crawl + upload)', free: true, pro: true, enterprise: true },
  { label: 'Conversations & lead capture', free: true, pro: true, enterprise: true },
  { label: 'Analytics dashboard', free: true, pro: true, enterprise: true },
  { label: 'Email & SMS new-chat alerts', free: true, pro: true, enterprise: true },
  { label: 'API keys & team roles', free: true, pro: true, enterprise: true },
  { label: 'Priority support', free: false, pro: true, enterprise: true },
  { label: 'Self-host on your infrastructure', free: true, pro: true, enterprise: true },
  { label: 'SSO & dedicated onboarding', free: false, pro: false, enterprise: true },
  { label: 'Voice integration', free: 'Unavailable', pro: 'Not enabled in this build', enterprise: 'Not enabled in this build' },
];

export const FAQ = [
  {
    q: 'Do I need a credit card to start?',
    a: 'No. The Free plan (2 team members, 1 widget) is self-serve — just verify your email with a 6-digit code. Self-hosting, your database, and model-provider usage may have separate costs.',
  },
  {
    q: 'How do I get Pro or Enterprise?',
    a: 'Pro is $15/month. Self-serve checkout isn’t available yet, so for Pro or Enterprise, contact us (Enterprise: use "Request a meeting") and a platform admin activates your plan. Your knowledge base, conversations, and leads carry over.',
  },
  {
    q: 'What does “grounded” mean?',
    a: 'The agent retrieves context from your knowledge base to inform its answers and provide source references. AI can still make mistakes, so review important answers against the original content.',
  },
  {
    q: 'Can I self-host?',
    a: 'Yes. The stack supports container-based deployment on your infrastructure. You configure the database and model provider; self-hosting alone does not make model processing offline.',
  },
  {
    q: 'Are voice and fine-tuning available?',
    a: 'Voice routes are not enabled in this build, and fine-tuning is currently a simulated workflow. The Trainer can generate and validate Q&A pairs, but it does not yet run a real model fine-tuning job.',
  },
];
